import React, { useState } from 'react';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Heart, Ruler, Scale, Activity, CheckCircle, XCircle,
  AlertTriangle, Info, Calculator, Target, TrendingUp
} from 'lucide-react';

export default function CabinCrewCareerFitness() {
  const [measurements, setMeasurements] = useState({
    height: '',
    weight: '',
    armReach: '',
    gender: 'female'
  });
  
  const [results, setResults] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Havayolu şirketlerinin gereksinimleri
  const airlineRequirements = {
    'THY': {
      name: 'Türk Hava Yolları',
      logo: '🇹🇷',
      female: { minHeight: 160, maxHeight: 180, armReach: 210 },
      male: { minHeight: 168, maxHeight: 190, armReach: 212 },
      bmi: { min: 18.5, max: 25 },
      notes: 'Yüzme bilmek zorunlu, düzeltilmiş görme 20/20'
    },
    'Emirates': {
      name: 'Emirates',
      logo: '🇦🇪',
      female: { minHeight: 160, maxHeight: null, armReach: 212 },
      male: { minHeight: 160, maxHeight: null, armReach: 212 },
      bmi: { min: 18, max: 25 },
      notes: 'Parmak ucunda 212 cm uzanabilme zorunlu'
    },
    'Qatar': {
      name: 'Qatar Airways',
      logo: '🇶🇦',
      female: { minHeight: 157, maxHeight: null, armReach: 212 },
      male: { minHeight: 168, maxHeight: null, armReach: 212 },
      bmi: { min: 18, max: 25 },
      notes: 'Kol uzanma testi mülakatta yapılır'
    },
    'Pegasus': {
      name: 'Pegasus',
      logo: '🟠',
      female: { minHeight: 160, maxHeight: 180, armReach: 210 },
      male: { minHeight: 168, maxHeight: 190, armReach: 210 },
      bmi: { min: 18.5, max: 26 },
      notes: 'Türkiye vatandaşlığı veya çalışma izni gerekli'
    },
    'SunExpress': {
      name: 'SunExpress',
      logo: '☀️',
      female: { minHeight: 160, maxHeight: 180, armReach: 210 },
      male: { minHeight: 168, maxHeight: 188, armReach: 210 },
      bmi: { min: 18.5, max: 25 },
      notes: 'Almanca bilmek avantaj'
    }
  };

  const calculateBMI = (weight, height) => {
    if (!weight || !height) return null;
    const heightInMeters = height / 100;
    return (weight / (heightInMeters * heightInMeters)).toFixed(1);
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { label: 'Zayıf', color: 'text-amber-600', bg: 'bg-amber-100' };
    if (bmi < 25) return { label: 'Normal', color: 'text-green-600', bg: 'bg-green-100' };
    if (bmi < 30) return { label: 'Fazla Kilolu', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { label: 'Obez', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const checkEligibility = () => {
    const height = parseFloat(measurements.height);
    const weight = parseFloat(measurements.weight);
    const armReach = parseFloat(measurements.armReach);
    const gender = measurements.gender;
    
    if (!height || !weight) {
      alert('Lütfen boy ve kilo bilgilerini girin');
      return;
    }

    const bmi = calculateBMI(weight, height);
    const bmiCategory = getBMICategory(bmi);
    
    const airlineResults = {};
    
    Object.entries(airlineRequirements).forEach(([key, airline]) => {
      const req = airline[gender];
      const checks = {
        height: {
          passed: height >= req.minHeight && (!req.maxHeight || height <= req.maxHeight),
          message: req.maxHeight 
            ? `Boy: ${req.minHeight}-${req.maxHeight} cm (Sizin: ${height} cm)`
            : `Min Boy: ${req.minHeight} cm (Sizin: ${height} cm)`
        },
        bmi: {
          passed: bmi >= airline.bmi.min && bmi <= airline.bmi.max,
          message: `BMI: ${airline.bmi.min}-${airline.bmi.max} (Sizin: ${bmi})`
        },
        armReach: {
          passed: !armReach || armReach >= req.armReach,
          message: `Kol Uzanma: Min ${req.armReach} cm ${armReach ? `(Sizin: ${armReach} cm)` : '(Ölçülmedi)'}`
        }
      };
      
      const overallPassed = checks.height.passed && checks.bmi.passed && (checks.armReach.passed);
      
      airlineResults[key] = {
        ...airline,
        checks,
        overallPassed,
        score: Object.values(checks).filter(c => c.passed).length
      };
    });

    setResults({
      bmi,
      bmiCategory,
      airlines: airlineResults
    });
  };

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 p-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
              <Heart className="w-8 h-8 text-red-500" />
              Fiziksel Yeterlilik Testleri
            </h1>
            <p className="text-gray-600">Havayolu şirketlerinin fiziksel gereksinimlerini kontrol edin</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Input Form */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-rose-500" />
                  Ölçümleriniz
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cinsiyet</label>
                  <div className="flex gap-2">
                    <Button
                      variant={measurements.gender === 'female' ? 'default' : 'outline'}
                      className={measurements.gender === 'female' ? 'bg-pink-500 flex-1' : 'flex-1'}
                      onClick={() => setMeasurements(prev => ({ ...prev, gender: 'female' }))}
                    >
                      👩 Kadın
                    </Button>
                    <Button
                      variant={measurements.gender === 'male' ? 'default' : 'outline'}
                      className={measurements.gender === 'male' ? 'bg-blue-500 flex-1' : 'flex-1'}
                      onClick={() => setMeasurements(prev => ({ ...prev, gender: 'male' }))}
                    >
                      👨 Erkek
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Ruler className="w-4 h-4 inline mr-1" /> Boy (cm) *
                  </label>
                  <Input
                    type="number"
                    value={measurements.height}
                    onChange={(e) => setMeasurements(prev => ({ ...prev, height: e.target.value }))}
                    placeholder="165"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Scale className="w-4 h-4 inline mr-1" /> Kilo (kg) *
                  </label>
                  <Input
                    type="number"
                    value={measurements.weight}
                    onChange={(e) => setMeasurements(prev => ({ ...prev, weight: e.target.value }))}
                    placeholder="58"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Activity className="w-4 h-4 inline mr-1" /> Kol Uzanma (cm)
                  </label>
                  <Input
                    type="number"
                    value={measurements.armReach}
                    onChange={(e) => setMeasurements(prev => ({ ...prev, armReach: e.target.value }))}
                    placeholder="212"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Parmak uçlarınızla yukarı uzandığınızda ulaştığınız yükseklik
                  </p>
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-600"
                  onClick={checkEligibility}
                >
                  <Target className="w-4 h-4 mr-2" />
                  Uygunluğumu Kontrol Et
                </Button>

                {/* BMI Info */}
                {measurements.height && measurements.weight && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">BMI (Vücut Kitle İndeksi)</span>
                      <span className="font-bold text-lg">
                        {calculateBMI(measurements.weight, measurements.height)}
                      </span>
                    </div>
                    <Progress 
                      value={Math.min(100, (calculateBMI(measurements.weight, measurements.height) / 35) * 100)} 
                      className="mt-2"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>18.5</span>
                      <span>25</span>
                      <span>30</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Havayolu Uygunluk Sonuçları
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!results ? (
                  <div className="text-center py-12 text-gray-500">
                    <Activity className="w-16 h-16 mx-auto mb-4 opacity-20" />
                    <p>Ölçümlerinizi girin ve "Uygunluğumu Kontrol Et" butonuna tıklayın</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* BMI Summary */}
                    <div className={`p-4 rounded-lg ${results.bmiCategory.bg}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">BMI Durumunuz</p>
                          <p className={`text-2xl font-bold ${results.bmiCategory.color}`}>
                            {results.bmi} - {results.bmiCategory.label}
                          </p>
                        </div>
                        <div className="text-4xl">
                          {results.bmiCategory.label === 'Normal' ? '✅' : '⚠️'}
                        </div>
                      </div>
                    </div>

                    {/* Airline Results */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(results.airlines).map(([key, airline]) => (
                        <div
                          key={key}
                          className={`p-4 rounded-lg border-2 ${
                            airline.overallPassed 
                              ? 'border-green-300 bg-green-50' 
                              : 'border-red-300 bg-red-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{airline.logo}</span>
                              <span className="font-bold">{airline.name}</span>
                            </div>
                            <Badge className={airline.overallPassed ? 'bg-green-500' : 'bg-red-500'}>
                              {airline.overallPassed ? 'UYGUN' : 'UYGUN DEĞİL'}
                            </Badge>
                          </div>
                          
                          <div className="space-y-2">
                            {Object.entries(airline.checks).map(([checkKey, check]) => (
                              <div key={checkKey} className="flex items-center gap-2 text-sm">
                                {check.passed ? (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-500" />
                                )}
                                <span className={check.passed ? 'text-green-700' : 'text-red-700'}>
                                  {check.message}
                                </span>
                              </div>
                            ))}
                          </div>
                          
                          <p className="text-xs text-gray-500 mt-3 pt-2 border-t">
                            {airline.notes}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Tips */}
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">Önemli Notlar:</p>
                            <ul className="list-disc list-inside space-y-1">
                              <li>Bu sonuçlar genel kriterlere dayanmaktadır, güncel bilgiler için şirket sitelerini kontrol edin</li>
                              <li>Kol uzanma testi genellikle mülakat sırasında yapılır</li>
                              <li>BMI hesaplaması için ideal değerler 18.5-25 arasındadır</li>
                              <li>Görme, işitme ve genel sağlık testleri ayrıca değerlendirilir</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Arm Reach Test Guide */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-500" />
                Kol Uzanma Testi Nasıl Yapılır?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">1️⃣</span>
                  </div>
                  <p className="font-medium">Duvara Yaslanın</p>
                  <p className="text-sm text-gray-500">Topuklarınız duvara değmeli</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">2️⃣</span>
                  </div>
                  <p className="font-medium">Parmak Ucuna Yükselin</p>
                  <p className="text-sm text-gray-500">Ayaklarınız düz, parmak uçlarında</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">3️⃣</span>
                  </div>
                  <p className="font-medium">Tek Kolunuzu Uzatın</p>
                  <p className="text-sm text-gray-500">Mümkün olduğunca yukarı uzanın</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">4️⃣</span>
                  </div>
                  <p className="font-medium">Ölçümü Alın</p>
                  <p className="text-sm text-gray-500">Parmak ucunuzun ulaştığı yükseklik</p>
                </div>
              </div>
              <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                <p className="text-amber-800 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  <strong>Not:</strong> Gerçek mülakatlarda bu test havayolu şirketi yetkilileri tarafından yapılır. 
                  Genellikle 212 cm yüksekliğindeki bir rafın altına ulaşmanız istenir.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </CabinCrewLayout>
  );
}
